import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { stocktakeSessionsService, beverageProductsService, siteInventoryService } from '../../services';
import ProductPicker from '../../components/ProductPicker';
import { subcategoryComparator } from '../../lib/subcategoryOrder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Package, ShoppingCart, RefreshCw, Plus, Trash2, Edit3, PackagePlus, X } from 'lucide-react';
import { formatCurrency, DataSourceBadge } from './stockAuditUtils';
import { getApiError } from '@/lib/utils';
import AddFromMasterModal from '../../components/AddFromMasterModal';
import stocktakeOfflineService from '../../services/stocktakeOfflineService';

const OPENING_SOURCE_LABELS = {
  psb_import: 'Imported from historical closing stock',
  legacy_snapshot: 'Imported from legacy closing snapshot',
  manual: 'Manually entered opening stock',
};

// Row-aware: "empty" only when there really are no opening rows.
export const openingSourceText = (items, sourceRef) => {
  if (sourceRef) return `Carried forward from ${sourceRef}`;
  if (!items.length) return 'No prior locked session found — opening stock empty';
  const types = [...new Set(items.map((i) => i.source_type).filter(Boolean))];
  if (types.length === 1) return OPENING_SOURCE_LABELS[types[0]] || 'Imported opening stock';
  return types.length ? 'Opening stock from more than one source' : 'Imported opening stock';
};


export const OpeningStockPanel = forwardRef(function OpeningStockPanel({ sessionId, siteId, status, draftPriorWarning, offlineData }, ref) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repopulating, setRepopulating] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [siteInventoryEmpty, setSiteInventoryEmpty] = useState(false);
  const manualEntryRef = useRef(null);
  const { toast } = useToast();

  // iter429 — Expose an imperative `save()` so the parent wizard can
  // flush pending debounced work when the auditor clicks Next / Back /
  // jumps steps / closes the tab. Mirrors WizardDeliveriesCreditsPanel,
  // WizardSalesPanel and AdjustmentsPanel. Fixes ST011 data loss where
  // typed opening-stock rows were dropped when the step changed within
  // the 2 s debounce window.
  useImperativeHandle(ref, () => ({
    async save() {
      try { await manualEntryRef.current?.save?.(); } catch { /* silent — best effort */ }
    },
  }));

  const load = useCallback(async () => {
    // Offline: use package data directly
    if (offlineData) {
      const items = (offlineData.opening_stock || []).map(os => {
        const prod = (offlineData.products || []).find(p => p.id === os.beverage_product_id);
        const displayQty = os.quantity || 0;
        const cat = prod?.category;
        return {
          ...os,
          product_name: prod?.product_name || 'Unknown product',
          product_code: prod?.product_code || '',
          category: typeof cat === 'object' ? cat?.value : cat,
          subcategory: prod?.subcategory || null,
          base_quantity: displayQty,
          unit_cost: os.value && displayQty ? (os.value / displayQty) : null,
        };
      });
      const total = items.reduce((sum, i) => sum + (parseFloat(i.value) || 0), 0);
      setData({ items, total, source_reference: items.length > 0 ? 'Prior session (offline package)' : null });
      setDataSource('package');
      setLoading(false);
      return;
    }
    try {
      const res = await stocktakeSessionsService.getOpeningStock(sessionId);
      setData(res);
      setDataSource('live-api');
      // If no items and no source reference, this is a first audit — show manual entry
      if ((!res.items || res.items.length === 0) && !res.source_reference) {
        setManualMode(true);
      }
      // iter340 v8 — Detect if the site has zero assigned inventory.
      // When true, the manual entry surfaces the FULL master list and we
      // show an amber banner explaining auto-assignment will occur.
      if (siteId) {
        try {
          const inv = await siteInventoryService.listInventory(siteId);
          const arr = Array.isArray(inv) ? inv : (inv?.items || []);
          setSiteInventoryEmpty(arr.length === 0);
        } catch { setSiteInventoryEmpty(false); }
      }
    } catch (err) {
      console.warn('[OpeningStock] LIVE API FAILED:', err.message);
    } finally { setLoading(false); }
  }, [sessionId, offlineData, siteId]);

  useEffect(() => { load(); }, [load]);

  const handleRepopulate = async () => {
    setRepopulating(true);
    try {
      const info = await stocktakeSessionsService.repopulateOpeningStock(sessionId);
      toast({ title: 'Opening Stock Updated', description: `${info.populated} items from ${info.source_reference || 'N/A'}` });
      setManualMode(false);
      load();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally { setRepopulating(false); }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const sourceRef = data?.source_reference;
  const editable = !['completed', 'locked'].includes(status);
  const hasOpeningStock = items.length > 0;
  const isManualSource = items.length > 0 && items[0]?.source_type === 'manual';

  // iter367 #4 — Sales-panel parity: pre-populate all site-assigned
  // products in the opening-stock list, with zero values for products
  // that weren't in the carry-forward. This way subcategorisation,
  // sorting, filtering and search behave identically across both
  // wizard panels. Products are augmented as read-only zero rows
  // (the auditor can still inline-add a quantity via the existing
  // "+ another row" / manual-add flow).
  // Built BEFORE the search/filter/group memos so they pick it up
  // transparently — the rest of the file keeps using `items` as the
  // canonical source.

  // iter360 — When the user's search returns 0 hits in the existing
  // opening-stock list, find any products that ARE in this site's
  // inventory but haven't been added to opening stock yet, and surface
  // them with a one-click "Add to opening stock" action. This closes
  // the UX gap where users typed a product name into the filter,
  // got no results, and assumed the product didn't exist — when really
  // it just hadn't been added to opening stock yet.
  const [allProducts, setAllProducts] = useState([]);
  const [siteProductIdsLookup, setSiteProductIdsLookup] = useState(new Set());
  const [pendingPickProductId, setPendingPickProductId] = useState(null);
  useEffect(() => {
    if (offlineData) {
      setAllProducts(offlineData?.products || []);
      const ids = new Set((offlineData?.site_inventory || []).map(i => String(i.beverage_product_id || i.id)));
      setSiteProductIdsLookup(ids);
      return;
    }
    beverageProductsService.listAll({})
      .then(d => setAllProducts(Array.isArray(d) ? d : (d?.items || d?.products || [])))
      .catch(() => {});
    if (siteId) {
      siteInventoryService.listInventory(siteId)
        .then(d => {
          const arr = Array.isArray(d) ? d : (d?.items || []);
          setSiteProductIdsLookup(new Set(arr.map(i => String(i.beverage_product_id || i.id))));
        })
        .catch(() => {});
    }
  }, [siteId, offlineData]);

  const handleAddSuggestionToOpening = useCallback((product) => {
    setPendingPickProductId(product.id);
    setManualMode(true);
    setSearchTerm('');
  }, []);

  // iter367 #4 — Sales-panel parity: augment the carry-forward items
  // list with zero-value placeholder rows for ANY product that is in
  // this site's inventory but missing from `items`. Downstream
  // `filteredItems` / `groupedItems` / `availableCategories` all pivot
  // off this augmented list, so subcategorisation + search + filter
  // behave identically to the Sales panel. The auditor can edit
  // quantities inline; rows untouched stay as zeros until save.
  const augmentedItems = useMemo(() => {
    if (!siteProductIdsLookup || siteProductIdsLookup.size === 0) return items;
    const haveSet = new Set(items.map(i => String(i.beverage_product_id)));
    const productById = new Map();
    allProducts.forEach(p => productById.set(String(p.id), p));
    const extras = [];
    siteProductIdsLookup.forEach(pidStr => {
      if (haveSet.has(pidStr)) return;
      const prod = productById.get(pidStr);
      if (!prod) return;
      const cat = prod.category;
      extras.push({
        id: null,
        beverage_product_id: Number(pidStr),
        product_name: prod.product_name || `Product ${pidStr}`,
        product_code: prod.product_code || '',
        category: typeof cat === 'object' ? cat?.value : cat,
        subcategory: prod.subcategory || null,
        quantity: 0,
        base_quantity: 0,
        value: 0,
        unit_cost: null,
        source_type: 'site_inventory_zero',
        _augmented: true,
      });
    });
    if (extras.length === 0) return items;
    return [...items, ...extras];
  }, [items, allProducts, siteProductIdsLookup]);

  const filteredItems = useMemo(() => {
    let result = [...augmentedItems];
    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(item =>
        (item.product_name || '').toLowerCase().includes(q) ||
        (item.product_code || '').toLowerCase().includes(q)
      );
    }
    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter(item => item.category && selectedCategories.includes(item.category));
    }
    // Subcategory filter
    if (selectedSubcategories.length > 0) {
      result = result.filter(item => item.subcategory && selectedSubcategories.includes(item.subcategory));
    }
    // Sort alphabetically by product name
    result.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
    return result;
  }, [augmentedItems, searchTerm, selectedCategories, selectedSubcategories]);

  const availableCategories = useMemo(() => {
    const set = new Set();
    augmentedItems.forEach(i => { if (i.category) set.add(i.category); });
    return [...set].sort();
  }, [augmentedItems]);
  const availableSubcategories = useMemo(() => {
    const set = new Set();
    augmentedItems.forEach(i => { if (i.subcategory) set.add(i.subcategory); });
    // Use canonical display order, NOT alphabetical (ST-0028 fix)
    return [...set].sort(subcategoryComparator);
  }, [augmentedItems]);
  const activeFilterCount = selectedCategories.length + selectedSubcategories.length;

  // iter360 — surface site-inventory products that match the user's
  // search but are NOT yet in opening stock. Declared AFTER filteredItems
  // (TDZ fix iter360b — the previous order caused
  // "Cannot access 'q' before initialization" in production builds).
  const missingFromOpeningStockSuggestions = useMemo(() => {
    if (!searchTerm || filteredItems.length > 0) return [];
    const q = searchTerm.toLowerCase();
    const inOpeningStock = new Set(items.map(i => String(i.beverage_product_id)));
    return allProducts.filter(p => {
      if (inOpeningStock.has(String(p.id))) return false;
      if (siteProductIdsLookup.size > 0 && !siteProductIdsLookup.has(String(p.id))) return false;
      const name = (p.product_name || '').toLowerCase();
      const code = (p.product_code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    }).slice(0, 8);
  }, [searchTerm, filteredItems, items, allProducts, siteProductIdsLookup]);

  // iter340 v7 — group filtered items by subcategory with a heading row,
  // products sorted alphabetically within each group. Items without a
  // subcategory fall under "Other" pinned last.
  const groupedItems = useMemo(() => {
    const groups = new Map();
    for (const item of filteredItems) {
      const sub = (item.subcategory && String(item.subcategory).trim()) || 'Other';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(item);
    }
    const keys = [...groups.keys()].sort(subcategoryComparator);
    return keys.map(k => ({ subcategory: k, items: groups.get(k) }));
  }, [filteredItems]);

  if (loading) return <Card><CardContent className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading opening stock...</CardContent></Card>;

  // Manual entry mode — first audit, no prior session
  if (manualMode && editable) {
    // iter341 — pass active filters down so the inline form respects
    // the same Search / Category / Sub-category narrowing the user had
    // in the table view. Filters and grouping persist across modes so
    // editing is contextual and doesn't dump the auditor back into the
    // full 200-product list.
    const visibleFilters = (
      <div className="space-y-2 pb-2 mb-2 border-b">
        <Input
          placeholder="Search by name or product code..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="h-8 text-sm"
          data-testid="opening-stock-search"
        />
        {(availableCategories.length > 0 || availableSubcategories.length > 0) && (
          <div className="space-y-1.5 p-2 bg-slate-50 rounded border border-slate-100 text-xs">
            {availableCategories.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-slate-500 mb-1">Category</p>
                <div className="flex flex-wrap gap-1">
                  {availableCategories.map(cat => {
                    const isActive = selectedCategories.includes(cat);
                    return (
                      <button key={cat}
                        onClick={() => setSelectedCategories(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                          isActive ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                        }`} data-testid={`opening-cat-${cat}`}>{String(cat).replace(/_/g, ' ')}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {availableSubcategories.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-slate-500 mb-1">Sub-category</p>
                <div className="flex flex-wrap gap-1">
                  {availableSubcategories.map(sub => {
                    const isActive = selectedSubcategories.includes(sub);
                    return (
                      <button key={sub}
                        onClick={() => setSelectedSubcategories(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                          isActive ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                        }`} data-testid={`opening-subcat-${sub}`}>{sub}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <button onClick={() => { setSelectedCategories([]); setSelectedSubcategories([]); }}
                className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5" data-testid="opening-clear-filters">
                <X className="w-2.5 h-2.5" /> Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        )}
      </div>
    );
    return (
      <Card data-testid="opening-stock-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />Opening Stock
            </CardTitle>
            {hasOpeningStock && (
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setManualMode(false)}>View Table</Button>
            )}
          </div>
          <CardDescription className="text-xs">
            {hasOpeningStock
              ? `Editing ${filteredItems.length} of ${items.length} items${activeFilterCount > 0 || searchTerm ? ' (filtered)' : ''}.`
              : 'No prior audit session found. Enter opening stock manually with cost per unit (excl. VAT).'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {siteInventoryEmpty && !offlineData && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800" data-testid="opening-stock-auto-assign-banner">
              <strong className="font-semibold">No site inventory yet —</strong>{' '}
              every product you save below will be automatically assigned to this site for the rest of the wizard and future stocktakes.
            </div>
          )}
          {hasOpeningStock && visibleFilters}
          <ManualOpeningStockEntry
            ref={manualEntryRef}
            sessionId={sessionId}
            siteId={siteId}
            existingItems={items}
            visibleProductIds={(activeFilterCount > 0 || searchTerm) ? new Set(filteredItems.map(i => i.beverage_product_id)) : null}
            groupedItems={hasOpeningStock ? groupedItems : null}
            onSaved={() => { setManualMode(false); setPendingPickProductId(null); load(); }}
            offlineData={offlineData}
            pendingPickProductId={pendingPickProductId}
            onPendingPickConsumed={() => setPendingPickProductId(null)}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="opening-stock-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Opening Stock
            {dataSource && <DataSourceBadge source={dataSource} />}
            {isManualSource && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700">Manual Entry</Badge>}
          </CardTitle>
          <div className="flex gap-1">
            {editable && !hasOpeningStock && !offlineData && (
              <Button size="sm" variant="outline" onClick={() => setManualMode(true)} data-testid="enter-manual-opening-btn">
                <Edit3 className="h-3 w-3 mr-1" />Enter Manually
              </Button>
            )}
            {editable && hasOpeningStock && !offlineData && (
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setManualMode(true)} data-testid="edit-manual-opening-btn">
                <Edit3 className="h-3 w-3 mr-1" />Edit
              </Button>
            )}
            {editable && !offlineData && (
              <Button size="sm" variant="outline" onClick={handleRepopulate} disabled={repopulating} data-testid="repopulate-opening-stock-btn">
                {repopulating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Re-populate
              </Button>
            )}
          </div>
        </div>
        <CardDescription className="text-xs" data-testid="opening-stock-source-text">
          {openingSourceText(items, sourceRef)}
          {items.length > 0 && ` | ${items.length} items`}
        </CardDescription>
        {draftPriorWarning && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs" data-testid="opening-stock-draft-warning">
            <p className="font-medium text-amber-800">Unposted Prior Session Detected</p>
            <p className="text-amber-700 mt-0.5">{draftPriorWarning}</p>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-blue-50 rounded-lg mb-3">
          <p className="text-xs text-muted-foreground">Opening Stock Value</p>
          <p className="text-2xl font-bold text-blue-700" data-testid="opening-stock-total">{formatCurrency(total)}</p>
        </div>
        {items.length > 0 && (
          <>
          {items.length > 0 && (
            <div className="mb-3 space-y-2">
              <Input
                placeholder="Search by name or product code..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="h-8 text-sm"
                data-testid="opening-stock-search"
              />
              {(availableCategories.length > 0 || availableSubcategories.length > 0) && (
                <div className="space-y-1.5 p-2 bg-slate-50 rounded border border-slate-100 text-xs">
                  {availableCategories.length > 0 && (
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">Category</p>
                      <div className="flex flex-wrap gap-1">
                        {availableCategories.map(cat => {
                          const isActive = selectedCategories.includes(cat);
                          return (
                            <button key={cat}
                              onClick={() => setSelectedCategories(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                                isActive ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`} data-testid={`opening-cat-${cat}`}>{String(cat).replace(/_/g, ' ')}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {availableSubcategories.length > 0 && (
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">Sub-category</p>
                      <div className="flex flex-wrap gap-1">
                        {availableSubcategories.map(sub => {
                          const isActive = selectedSubcategories.includes(sub);
                          return (
                            <button key={sub}
                              onClick={() => setSelectedSubcategories(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                                isActive ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`} data-testid={`opening-subcat-${sub}`}>{sub}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {activeFilterCount > 0 && (
                    <button onClick={() => { setSelectedCategories([]); setSelectedSubcategories([]); }}
                      className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5" data-testid="opening-clear-filters">
                      <X className="w-2.5 h-2.5" /> Clear filters ({activeFilterCount})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="opening-stock-table">
              <thead>
                <tr className="bg-slate-50 border-b text-xs text-muted-foreground">
                  <th className="text-left px-3 py-2 font-medium w-20">Code</th>
                  <th className="text-left px-3 py-2 font-medium">Product</th>
                  <th className="text-left px-3 py-2 font-medium">Unit</th>
                  <th className="text-right px-3 py-2 font-medium">Qty</th>
                  <th className="text-right px-3 py-2 font-medium">Unit Cost</th>
                  <th className="text-right px-3 py-2 font-medium">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {groupedItems.map(group => (
                  <React.Fragment key={`grp-${group.subcategory}`}>
                    <tr className="bg-slate-100/95" data-testid={`opening-subcategory-group-${group.subcategory.replace(/\s+/g, '-').toLowerCase()}`}>
                      <td colSpan={6} className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                        {group.subcategory}
                        <span className="ml-2 text-slate-400 normal-case font-normal">({group.items.length})</span>
                      </td>
                    </tr>
                    {group.items.map((item) => {
                      // iter341 — round-trip the value the user actually
                      // entered (count-unit qty + unit label). Falling
                      // back to base_quantity here showed Jagermeister as
                      // "92.4 bottle" instead of "3.3 bottle". We still
                      // surface the converted stock-unit value as a
                      // tiny preview so auditors can sanity-check the
                      // configured conversion.
                      const displayQty = item.quantity ?? item.base_quantity;
                      const displayUnit = item.unit_label || item.base_unit || '';
                      const showConversionPreview = (
                        item.quantity != null
                        && item.base_quantity != null
                        && item.base_unit
                        && (item.unit_label || '').toLowerCase() !== (item.base_unit || '').toLowerCase()
                        && Math.abs(Number(item.quantity) - Number(item.base_quantity)) > 0.0001
                      );
                      return (
                      <tr key={item.id ?? `${item.beverage_product_id}-${item.product_name}`} className="hover:bg-slate-50/50" data-testid={`opening-stock-row-${item.beverage_product_id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500" data-testid={`opening-stock-code-${item.beverage_product_id}`}>{item.product_code || '-'}</td>
                        <td className="px-3 py-2">
                          {item.product_name || 'Unknown product'}
                          {item.value === 0 && (
                            <Badge variant="outline" className="ml-1 text-[8px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`zero-cost-badge-${item.beverage_product_id}`}>£0</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{displayUnit}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {displayQty}
                          {showConversionPreview && (
                            <div className="text-[9px] text-muted-foreground" data-testid={`opening-stock-base-qty-${item.beverage_product_id}`}>
                              = {Number(item.base_quantity).toFixed(2)} {item.base_unit}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{item.unit_cost ? formatCurrency(item.unit_cost) : '-'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency(item.value)}</td>
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {filteredItems.length === 0 && (searchTerm || activeFilterCount > 0) && (
                  <tr><td colSpan={6} className="px-3 py-4">
                    <div className="text-center text-sm text-muted-foreground mb-2">
                      No existing opening-stock rows match your filters.
                    </div>
                    {/* iter360 — Suggest products that are in site inventory but not in opening stock */}
                    {missingFromOpeningStockSuggestions.length > 0 && editable && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 max-w-2xl mx-auto" data-testid="missing-from-opening-suggestions">
                        <p className="text-sm text-blue-900 mb-2 font-medium">
                          The following product{missingFromOpeningStockSuggestions.length > 1 ? 's are' : ' is'} in this site's inventory but {missingFromOpeningStockSuggestions.length > 1 ? "haven't" : "hasn't"} been added to opening stock yet:
                        </p>
                        <div className="space-y-1.5">
                          {missingFromOpeningStockSuggestions.map(p => (
                            <button
                              key={p.id}
                              onClick={() => handleAddSuggestionToOpening(p)}
                              className="w-full text-left px-3 py-1.5 bg-white border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-400 transition flex items-center justify-between text-sm"
                              data-testid={`add-missing-${p.id}-btn`}
                            >
                              <span>
                                {p.product_code && <span className="font-mono text-xs text-slate-500 mr-2">{p.product_code}</span>}
                                <span className="font-medium">{p.product_name}</span>
                                {p.subcategory && <span className="text-xs text-slate-500 ml-2">· {p.subcategory}</span>}
                              </span>
                              <span className="text-xs text-blue-700 font-medium">+ Add to opening stock</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
        {!hasOpeningStock && !manualMode && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-2">No opening stock entries. This appears to be the first audit for this site.</p>
            {editable && (
              <Button variant="outline" onClick={() => setManualMode(true)} data-testid="enter-manual-opening-btn-empty">
                <Edit3 className="h-4 w-4 mr-1" />Enter Opening Stock Manually
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

/**
 * ManualOpeningStockEntry — inline form to enter opening stock per product.
 * Auto-saves on 2s debounce. Products filtered to site inventory.
 *
 * iter429 — Now `forwardRef` with an imperative `save()` handle so the
 * parent wizard can flush the pending debounced save before step
 * navigation or tab close. Fixes ST011 data loss.
 */
const ManualOpeningStockEntry = forwardRef(function ManualOpeningStockEntry({ sessionId, siteId, existingItems, onSaved, offlineData = null, visibleProductIds = null, groupedItems = null, pendingPickProductId = null, onPendingPickConsumed = null }, ref) {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [siteProductIds, setSiteProductIds] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const autoSaveRef = useRef(null);
  const initialLoadRef = useRef(true);
  const savingRef = useRef(false);
  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);
  const [zeroCostNames, setZeroCostNames] = useState([]);
  const isOfflineMode = !!offlineData;

  const handleProductAssignedFromMaster = useCallback(() => {
    if (!siteId || isOfflineMode) return;
    siteInventoryService.listInventory(siteId)
      .then(data => {
        const ids = new Set((data?.items || data || []).map(i => String(i.beverage_product_id || i.id)));
        setSiteProductIds(ids);
      })
      .catch(() => {});
  }, [siteId, isOfflineMode]);

  // iter340 v2 — offline AddFromMaster: queue the assignment and locally
  // extend siteProductIds + products so the picker shows the new product
  // immediately. Server-side, the next sync will create the SiteBeverageInventory
  // row and reconcile the count sheet.
  const handleOfflineMasterAssign = useCallback(async (product) => {
    try {
      await stocktakeOfflineService.queueOfflineSiteInventoryAddition(Number(sessionId), product);
    } catch (e) {
      console.error('Failed to queue offline addition', e);
    }
    setSiteProductIds(prev => {
      const next = new Set(prev || []);
      next.add(String(product.id));
      return next;
    });
    setProducts(prev => prev.some(p => p.id === product.id) ? prev : [...prev, product]);
  }, [sessionId]);

  // Load products and site inventory
  useEffect(() => {
    if (isOfflineMode) {
      // Read from the offline package — no network calls.
      setProducts(offlineData?.products || []);
      const ids = new Set(
        (offlineData?.site_inventory || []).map(i => String(i.beverage_product_id || i.id))
      );
      setSiteProductIds(ids);
      return;
    }
    beverageProductsService.listAll({})  // iter360 — no status filter; site-inventory gate still applies. Hiding "inactive" master products silently dropped site-assigned products whose status was anything other than 'active' (NULL, missing, or inactive).
      .then(d => setProducts(Array.isArray(d) ? d : (d?.items || d?.products || [])))
      .catch(() => {});
    if (siteId) {
      siteInventoryService.listInventory(siteId)
        .then(data => {
          const ids = new Set((data?.items || data || []).map(i => String(i.beverage_product_id || i.id)));
          setSiteProductIds(ids);
        })
        .catch(() => setSiteProductIds(new Set()));
    }
  }, [siteId, isOfflineMode, offlineData]);

  const filteredProducts = useMemo(() => {
    if (!siteProductIds || siteProductIds.size === 0) return products;
    return products.filter(p => siteProductIds.has(String(p.id)));
  }, [products, siteProductIds]);

  const productMap = useMemo(() => {
    const m = {};
    products.forEach(p => { m[p.id] = p; });
    return m;
  }, [products]);

  // Initialize rows from existing items
  useEffect(() => {
    if (existingItems && existingItems.length > 0) {
      setRows(existingItems.map(item => ({
        beverage_product_id: String(item.beverage_product_id),
        // Show the count-unit quantity (e.g. "1200 pints") — NOT base_quantity,
        // which is in stock units (e.g. "13.63 kegs"). Fall back to base_quantity
        // only for rows missing a count quantity.
        quantity: String(item.quantity ?? item.base_quantity ?? ''),
        unit_label: item.unit_label || item.base_unit || '',
        unit_cost: item.unit_cost ? String(item.unit_cost) : '',
        _key: Math.random(),
      })));
    } else {
      setRows([newRow()]);
    }
  }, [existingItems]);

  function newRow() {
    // iter341 — `_isNew` marks rows added via the "Add Row" button so
    // we can pin them to the bottom of the form regardless of the
    // active sub-category grouping or filter narrowing. They re-bucket
    // into the right sub-category only after save+reload, when the
    // backend tells us their canonical subcategory.
    return { beverage_product_id: '', quantity: '', unit_label: '', unit_cost: '', _key: Math.random(), _isNew: true };
  }

  // iter360 — When the parent passes pendingPickProductId (user clicked
  // "Add to opening stock" on a suggestion in the read-view empty state),
  // append a pre-filled new row and consume the prop so it doesn't fire
  // again. Run after products are loaded so we can pull the unit_label.
  useEffect(() => {
    if (!pendingPickProductId || products.length === 0) return;
    const prod = products.find(p => String(p.id) === String(pendingPickProductId));
    if (!prod) return;
    setRows(prev => [
      ...prev,
      { ...newRow(), beverage_product_id: prod.id, unit_label: prod.stock_unit || prod.count_unit || '' },
    ]);
    onPendingPickConsumed && onPendingPickConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPickProductId, products]);

  const getDefaultUnit = (prod) => {
    if (!prod?.units?.length) return '';
    const stockUnit = prod.units.find(u => u.is_base_stock_unit);
    if (stockUnit) return stockUnit.unit_type;
    const sorted = [...prod.units].sort((a, b) => (parseFloat(b.conversion_to_base) || 0) - (parseFloat(a.conversion_to_base) || 0));
    return sorted[0]?.unit_type || '';
  };

  const getUnitOptions = (productId) => {
    const prod = productMap[parseInt(productId)];
    if (!prod?.units?.length) return [];
    return prod.units.map(u => {
      let label = u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1);
      if (parseFloat(u.conversion_to_base) > 1 && u.unit_type !== 'case') label += ` (${u.conversion_to_base}ml)`;
      return { value: u.unit_type, label };
    });
  };

  const handleProductChange = (idx, productId) => {
    const prod = productMap[parseInt(productId)];
    const unit = prod ? getDefaultUnit(prod) : '';
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, beverage_product_id: productId, unit_label: unit } : r));
  };

  const updateRow = (idx, field, val) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const addRow = () => {
    // iter363b — Add a new empty row AND immediately move the cursor
    // into its product picker so the auditor can start typing the
    // product name without having to mouse-click the new row. The
    // new row is always appended, so its index is `prev.length`.
    // We only FOCUS the trigger (not auto-open the popover) because
    // (a) Radix Popover doesn't reliably open from a synthetic click
    // event, and (b) keeping it closed lets the auditor press
    // Enter/Space to expand on demand — a single extra key for
    // predictable behaviour across browsers.
    setRows(prev => {
      const newIdx = prev.length;
      setTimeout(() => {
        const trigger = document.querySelector(`[data-testid="os-product-${newIdx}"]`);
        if (trigger && typeof trigger.focus === 'function') {
          trigger.focus();
        }
      }, 80);
      return [...prev, newRow()];
    });
  };
  const removeRow = (idx) => setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const totalValue = rows.reduce((sum, r) => {
    const q = parseFloat(r.quantity) || 0;
    const c = parseFloat(r.unit_cost) || 0;
    return sum + q * c;
  }, 0);

  // iter364 — Running totals per product, calculated cumulatively across
  // `rows` in original index order, expressed in the product's STOCK
  // unit. Only emitted when the product appears on more than one row
  // (single-product rows don't need a running line). Mirrors the
  // calculation used in the Sales panel + the audit engine:
  //   qty_in_stock = qty × (source_unit.conversion_to_base
  //                          / stock_unit.conversion_to_base)
  const runningStockByRowIdx = useMemo(() => {
    const stockUnitOf = (prod) => {
      if (!prod?.units?.length) return null;
      return prod.units.find(u => u.is_base_stock_unit)
        || prod.units.find(u => u.is_default_count_unit)
        || prod.units[0];
    };
    const counts = new Map();
    const running = new Map();
    const out = new Map();
    rows.forEach(r => {
      const pid = r.beverage_product_id ? String(r.beverage_product_id) : null;
      if (!pid) return;
      counts.set(pid, (counts.get(pid) || 0) + 1);
    });
    rows.forEach((r, idx) => {
      const pid = r.beverage_product_id ? String(r.beverage_product_id) : null;
      if (!pid) return;
      const prod = productMap[parseInt(pid)];
      const stockUnit = stockUnitOf(prod);
      const sourceUnit = prod?.units?.find(u =>
        (u.unit_type || '').toLowerCase() === (r.unit_label || '').toLowerCase()
      );
      const qty = parseFloat(r.quantity) || 0;
      let qtyInStock = qty;
      if (sourceUnit && stockUnit && sourceUnit.id !== stockUnit.id) {
        const src = parseFloat(sourceUnit.conversion_to_base) || 1;
        const stk = parseFloat(stockUnit.conversion_to_base) || 1;
        if (stk > 0) qtyInStock = (qty * src) / stk;
      }
      const prev = running.get(pid) || 0;
      const newRun = prev + qtyInStock;
      running.set(pid, newRun);
      if ((counts.get(pid) || 0) > 1) {
        out.set(idx, {
          running: newRun,
          stockUnit: stockUnit?.unit_type || 'unit',
          productCount: counts.get(pid),
        });
      }
    });
    return out;
  }, [rows, productMap]);

  // iter341 — Render helper for one entry row. Promoted out of the
  // map() so the grouped layout can call it from inside subcategory
  // buckets without duplicating the JSX.
  const renderEntryRow = (row, idx) => {
    const unitOpts = getUnitOptions(row.beverage_product_id);
    const qty = parseFloat(row.quantity);
    const cost = parseFloat(row.unit_cost);
    const lineValue = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(cost) ? cost : 0);
    const isZeroCost = (
      Number.isFinite(qty) && qty > 0
      && (!Number.isFinite(cost) || cost <= 0)
    );
    const running = runningStockByRowIdx.get(idx);
    const prod = row.beverage_product_id ? productMap[parseInt(row.beverage_product_id)] : null;
    return (
      <div key={row._key || idx} data-testid={`manual-os-rowwrap-${idx}`}>
      <div
        className={`flex gap-2 items-start ${isZeroCost ? 'border-l-2 border-amber-400 pl-1 bg-amber-50/30' : ''}`}
        data-testid={`manual-os-row-${idx}`}
      >
        <div className="flex-[3]">
          <ProductPicker
            products={filteredProducts}
            value={row.beverage_product_id}
            onChange={v => handleProductChange(idx, v)}
            placeholder="Select product..."
            testId={`os-product-${idx}`}
            onAfterSelect={() => {
              // iter363b — after the auditor picks a product, jump
              // focus to the Qty field on the same row so Tab/Enter
              // flows the way a paper stock-sheet would. Without
              // this, focus ends up on the page logo because the
              // popover trigger is the last focusable element
              // inside its column wrapper.
              const qty = document.querySelector(`[data-testid="os-qty-${idx}"]`);
              if (qty && typeof qty.focus === 'function') {
                qty.focus();
                if (typeof qty.select === 'function') qty.select();
              }
            }}
          />
        </div>
        <Input
          type="number" step="0.01" min="0" className="h-9 w-16 text-sm"
          placeholder="Qty" value={row.quantity}
          onChange={e => updateRow(idx, 'quantity', e.target.value)}
          data-testid={`os-qty-${idx}`}
        />
        {unitOpts.length > 0 ? (
          <Select value={row.unit_label || 'none'} onValueChange={v => updateRow(idx, 'unit_label', v === 'none' ? '' : v)}>
            <SelectTrigger className="h-9 w-28 text-xs" data-testid={`os-unit-${idx}`}><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent>
              {unitOpts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input className="h-9 w-28 text-xs" placeholder="Unit" value={row.unit_label} onChange={e => updateRow(idx, 'unit_label', e.target.value)} />
        )}
        <Input
          type="number" step="0.01" min="0"
          className={`h-9 w-24 text-sm ${isZeroCost ? 'border-amber-400 ring-amber-200' : ''}`}
          placeholder="Cost/unit" value={row.unit_cost}
          onChange={e => updateRow(idx, 'unit_cost', e.target.value)}
          title={isZeroCost ? 'Cost is zero — row will be saved with £0 (not dropped)' : undefined}
          data-testid={`os-cost-${idx}`}
        />
        <div className="w-20 h-9 flex items-center text-xs font-medium tabular-nums">
          {lineValue > 0 ? formatCurrency(lineValue) : '-'}
        </div>
        <Button size="icon" variant="ghost" className="h-9 w-7 text-red-400 shrink-0" onClick={() => removeRow(idx)}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {/* iter364 — running-balance hint + add-another-row button.
          Only rendered when the product is set AND this product
          appears on more than one row (i.e. the running total is
          actually useful). */}
      {prod && (running || true) && (
        <div className="flex items-center justify-end gap-3 pr-9 -mt-0.5 pb-1" data-testid={`os-row-footer-${idx}`}>
          {running && (
            <span
              className="text-[10px] text-slate-500 tabular-nums"
              data-testid={`os-row-running-${idx}`}
              title={`Running total across all ${running.productCount} rows for this product, in the product's stock unit.`}
            >
              running: <span className="font-mono text-slate-700">
                {(Math.round(running.running * 100) / 100).toLocaleString('en-GB', { maximumFractionDigits: 2 })}
              </span> {running.stockUnit}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setRows(prev => {
                const src = prev[idx];
                if (!src || !src.beverage_product_id) return prev;
                const newR = {
                  ...newRow(),
                  beverage_product_id: src.beverage_product_id,
                  unit_label: src.unit_label || '',
                  unit_cost: src.unit_cost || '',
                };
                return [...prev.slice(0, idx + 1), newR, ...prev.slice(idx + 1)];
              });
            }}
            className="text-[10px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
            data-testid={`os-row-another-${idx}`}
            title="Add another opening-stock row for the same product (e.g. cellar + bar split)"
          >
            + another line
          </button>
        </div>
      )}
      </div>
    );
  };

  // Auto-save on debounce
  const doSave = useCallback(async () => {
    if (savingRef.current) return;
    // iter341 — NEVER drop rows silently. The previous filter rejected
    // any row with quantity > 0 but missing/empty unit_cost — those
    // rows would disappear after save (e.g. WITCHMARK VODKA / LUXARDO
    // KIRSCH on Forest Lodge), erasing data the auditor had keyed.
    // Now we keep every row with a valid product + qty > 0, defaulting
    // missing cost to 0 and surfacing a non-blocking toast listing the
    // zero-cost products.
    const candidateRows = rows.filter(r => {
      if (!r.beverage_product_id) return false;
      const qty = parseFloat(r.quantity);
      return Number.isFinite(qty) && qty > 0;
    });
    if (candidateRows.length === 0) return;
    const zeroCostRows = candidateRows.filter(r => {
      const cost = parseFloat(r.unit_cost);
      return !Number.isFinite(cost) || cost <= 0;
    });
    savingRef.current = true;
    setSaving(true);
    try {
      const saveResult = await stocktakeSessionsService.saveManualOpeningStock(sessionId, candidateRows.map(r => {
        const cost = parseFloat(r.unit_cost);
        return {
          beverage_product_id: parseInt(r.beverage_product_id),
          quantity: parseFloat(r.quantity),
          unit_label: r.unit_label || null,
          unit_cost: Number.isFinite(cost) && cost >= 0 ? cost : 0,
        };
      }));
      // iter_fix3_v3 — Site-level count unit auto-persisted by backend.
      // Offer optional GLOBAL master update for each applied change.
      const appliedSiteUpdates = saveResult?.applied_site_updates || [];
      if (saveResult?.site_count_units && Object.keys(saveResult.site_count_units).length) {
        window.dispatchEvent(new CustomEvent('site-count-units-saved', { detail: { sessionId, units: saveResult.site_count_units } }));
      }
      for (const s of appliedSiteUpdates) {
        if (!s.to_unit_id) continue;
        const prodName = productMap[s.product_id]?.product_name || `Product #${s.product_id}`;
        const unitLabel = s.to_unit || '';
        toast({
          title: `Site count unit saved: ${prodName}`,
          description: `Opening stock unit "${unitLabel}" saved for this site. Also update the global product master?`,
          duration: 15000,
          action: (
            <button
              className="text-xs font-medium px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 ml-2"
              onClick={async () => {
                try {
                  await stocktakeSessionsService.updateGlobalCountUnit(s.product_id, s.to_unit_id);
                  toast({ title: 'Global master updated', description: `Default count unit for "${prodName}" set to "${unitLabel}" globally` });
                } catch (e) {
                  toast({ title: 'Could not update global master', description: getApiError(e, 'Update failed'), variant: 'destructive' });
                }
              }}
            >
              Update Master
            </button>
          ),
        });
      }
      // Update zero-cost names so caller can render a non-blocking warning.
      const names = zeroCostRows
        .map(r => productMap[parseInt(r.beverage_product_id)]?.product_name)
        .filter(Boolean);
      setZeroCostNames(names);
    } catch (err) {
      toast({ title: 'Error saving opening stock', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally { savingRef.current = false; setSaving(false); }
  }, [rows, sessionId, toast, productMap]);

  useEffect(() => {
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(doSave, 2000);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [rows, doSave]);

  // iter429 — Imperative flush so the parent wizard can force-save
  // pending edits before step nav / tab close. Cancels the debounce
  // timer and awaits `doSave` so callers can `await` confidently.
  useImperativeHandle(ref, () => ({
    async save() {
      if (autoSaveRef.current) { clearTimeout(autoSaveRef.current); autoSaveRef.current = null; }
      try { await doSave(); } catch { /* silent — best effort */ }
    },
  }), [doSave]);

  return (
    <div className="space-y-3" data-testid="manual-opening-stock-entry">
      {/* iter341 — non-blocking warning when zero/missing cost rows
          were saved. Keeps the data, but flags it so auditors can
          come back and key the cost without losing the entry. */}
      {zeroCostNames.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="opening-stock-zero-cost-warning">
          <span className="font-semibold">Saved with £0 cost ({zeroCostNames.length}):</span>
          <span className="flex-1">{zeroCostNames.slice(0, 6).join(', ')}{zeroCostNames.length > 6 ? `, +${zeroCostNames.length - 6} more` : ''}. Add a unit cost when you have it — quantities are preserved.</span>
          <button onClick={() => setZeroCostNames([])} className="text-amber-700 hover:text-amber-900" data-testid="dismiss-zero-cost-warning">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Manual Opening Stock Entry</p>
          <p className="text-xs text-muted-foreground">Enter quantities and cost per unit (excl. VAT) for each product. <span className="text-amber-700">Rows with no cost are saved with £0 — they will not be silently dropped.</span></p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Saving...</span>}
          <span className="text-sm font-medium tabular-nums">Total: {formatCurrency(totalValue)}</span>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground flex gap-2 px-1">
        <div className="flex-[3]">Product</div>
        <div className="w-16">Qty</div>
        <div className="w-28">Unit</div>
        <div className="w-24">Cost/Unit (ex-VAT)</div>
        <div className="w-20">Value</div>
        <div className="w-7"></div>
      </div>

      {(() => {
        // iter341 — group + sort rows the same way the view-mode table
        // does so editing keeps the auditor's mental model intact.
        // Existing rows (loaded from the server) get bucketed into
        // their sub-category and respect any active filter. ROWS ADDED
        // BY THE AUDITOR IN THIS EDIT SESSION (`_isNew: true`) are
        // ALWAYS rendered in a "+ New additions" group at the END,
        // never filtered out, never re-bucketed mid-edit — otherwise
        // picking a product (e.g. Luxardo Kirsch) would seemingly
        // "disappear" because the new product is not in the existing
        // visibleProductIds set or jumps into a sub-category bucket
        // mid-row.
        const subcatOf = (pid) => {
          const prod = productMap[parseInt(pid)];
          return (prod?.subcategory && String(prod.subcategory).trim()) || 'Other';
        };
        const visible = visibleProductIds;
        const indexed = rows.map((r, idx) => ({ row: r, idx }));
        const buckets = new Map();
        const newAdditions = [];
        const placeholderRows = []; // empty new rows with no product picked yet
        for (const it of indexed) {
          if (it.row._isNew) {
            // pinned to bottom
            if (!it.row.beverage_product_id) {
              placeholderRows.push(it);
            } else {
              newAdditions.push(it);
            }
            continue;
          }
          if (!it.row.beverage_product_id) {
            placeholderRows.push(it);
            continue;
          }
          if (visible && !visible.has(parseInt(it.row.beverage_product_id))) {
            continue;
          }
          const sub = subcatOf(it.row.beverage_product_id);
          if (!buckets.has(sub)) buckets.set(sub, []);
          buckets.get(sub).push(it);
        }
        const subKeys = [...buckets.keys()].sort((a, b) => {
          if (a === 'Other') return 1;
          if (b === 'Other') return -1;
          return a.localeCompare(b);
        });
        const totalNew = newAdditions.length + placeholderRows.length;
        return (
          <>
            {subKeys.map((sub) => {
              const bucket = buckets.get(sub);
              return (
                <React.Fragment key={`os-grp-${sub}`}>
                  <div className="bg-slate-100/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600 rounded" data-testid={`os-edit-subgroup-${sub.replace(/\s+/g, '-').toLowerCase()}`}>
                    {sub} <span className="text-slate-400 normal-case font-normal">({bucket.length})</span>
                  </div>
                  {bucket.map(({ row, idx }) => renderEntryRow(row, idx))}
                </React.Fragment>
              );
            })}
            {totalNew > 0 && (
              <>
                <div className="bg-emerald-100/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 rounded mt-2" data-testid="os-edit-new-additions-header">
                  + New additions <span className="text-emerald-600 normal-case font-normal">({totalNew}) — auto-bucketed by sub-category after save</span>
                </div>
                {newAdditions.map(({ row, idx }) => renderEntryRow(row, idx))}
                {placeholderRows.map(({ row, idx }) => renderEntryRow(row, idx))}
              </>
            )}
          </>
        );
      })()}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="add-os-row-btn">
            <Plus className="h-3 w-3 mr-1" />Add Product
          </Button>
          {/* iter340 v5 — Inventory master access moved to the Site
              Inventory Setup floating window in the wizard header. */}
        </div>
        <Button size="sm" variant="outline" onClick={() => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); doSave().then(onSaved); }} data-testid="done-manual-os-btn">
          Done
        </Button>
      </div>
      <AddFromMasterModal
        open={addFromMasterOpen}
        onOpenChange={setAddFromMasterOpen}
        siteId={siteId}
        assignedProductIds={[...(siteProductIds || [])].map(Number)}
        onAssigned={handleProductAssignedFromMaster}
        offlineProducts={isOfflineMode ? (offlineData?.products || []) : null}
        onOfflineAssign={isOfflineMode ? handleOfflineMasterAssign : null}
      />
    </div>
  );
});

// Sales Link Panel (points to standalone Sales module)
export function SalesLinkPanel({ sessionId, siteId }) {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { salesService } = await import('../../services');
        const res = await salesService.list({ site_id: siteId, status: 'posted', limit: 100 });
        if (!cancelled) setSalesData(res);
      } catch {} finally { if (!cancelled) setLoading(false); }
    };
    if (siteId) load();
    else setLoading(false);
    return () => { cancelled = true; };
  }, [siteId]);

  const postedSales = salesData?.items || [];
  const totalRevenue = postedSales.reduce((sum, s) => sum + (s.lines_total || 0), 0);

  return (
    <Card data-testid="sales-link-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-rose-600" />
            Sales
          </CardTitle>
          <a href="/stocktaking/sales" className="text-sm text-blue-600 hover:underline" data-testid="go-to-sales-link">
            Open Sales Register &rarr;
          </a>
        </div>
        <CardDescription className="text-xs">
          Sales are recorded in the standalone Sales module. Posted sales for this site are included in the stock audit calculation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-muted-foreground py-4"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading...</div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-rose-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Posted Sales (site)</p>
              <p className="text-lg font-bold text-rose-700" data-testid="posted-sales-count">{postedSales.length}</p>
            </div>
            <div className="bg-rose-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Total Revenue</p>
              <p className="text-lg font-bold text-rose-700" data-testid="posted-sales-total">{formatCurrency(totalRevenue)}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
